import React from 'react';
import { usePortfolio } from '../../context/PortfolioContext';
import { Skill } from '../../domain/Skill';
import { TechStackCarousel } from './TechStackCarousel';
import {
  Cpu,
  Code2,
  Terminal,
  Database,
  Globe,
  Network,
  Cloud,
  Wrench,
  GitBranch,
  Zap,
  Box,
  Layers,
  Palette,
  FileCode,
  Video
} from 'lucide-react';

const iconMap: Record<string, React.ReactNode> = {
  Code: <Code2 size={18} />,
  Palette: <Palette size={18} />,
  Video: <Video size={18} />,
  Zap: <Zap size={18} />,
  FileCode: <FileCode size={18} />,
  Layers: <Layers size={18} />,
  Globe: <Globe size={18} />,
  Network: <Network size={18} />,
  Terminal: <Terminal size={18} />,
  Cloud: <Cloud size={18} />,
  Wrench: <Wrench size={18} />,
  Database: <Database size={18} />,
  GitBranch: <GitBranch size={18} />,
  Box: <Box size={18} />
};

export const SkillsSection: React.FC = () => {
  const { skills } = usePortfolio();

  const webDevSkills = skills.filter((s: Skill) => s.category === 'web-development');
  const itInfraSkills = skills.filter((s: Skill) => s.category === 'it-infrastructure');
  const databaseSkills = skills.filter((s: Skill) => s.category === 'databases-tools');

  const gearTools = [
    { name: 'VS Code', icon: <Code2 size={24} /> },
    { name: 'Git & GitHub', icon: <GitBranch size={24} /> },
    { name: 'Canva Design', icon: <Palette size={24} /> },
    { name: 'CapCut Video', icon: <Video size={24} /> },
    { name: 'Postman / API', icon: <Globe size={24} /> },
    { name: 'Cloudflare Edge', icon: <Cloud size={24} /> },
    { name: 'MySQL / SQL', icon: <Database size={24} /> },
    { name: 'Terminal / CLI', icon: <Terminal size={24} /> }
  ];

  return (
    <section id="skills" className="skills">
      <div className="container">
        <div className="section-title-wrap" style={{ textAlign: 'center' }}>
          <div className="section-tag">
            <Cpu size={14} />
            <span>Technical Capabilities</span>
          </div>
          <h2 className="section-title">
            Programming Languages & <span>Core Skills</span>
          </h2>
          <p className="section-subtitle">
            Focused on practical fundamentals in Web Development, IT Support, Graphic Design (Canva), and Video Editing (CapCut). I actively leverage AI-Assisted tools (Claude Code, Google AntiGravity, OpenAI Codex) to build clean applications.
          </p>
        </div>

        <div className="skills-layout">
          {/* Web Dev & Programming Languages Card */}
          <div className="skills-card">
            <div className="skills-card-header">
              <div className="skills-icon-wrap">
                <Code2 size={22} />
              </div>
              <div>
                <h3 className="skills-card-title">Programming Languages & Frontend</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Core Languages & Framework Basics</p>
              </div>
            </div>

            <div className="skills-list">
              {webDevSkills.map((skill: Skill) => (
                <div key={skill.id} className="skill-item">
                  <div className="skill-info">
                    <span className="skill-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {iconMap[skill.iconName] || <Code2 size={18} />}
                      {skill.name}
                    </span>
                    <span className="section-tag" style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem', margin: 0, background: 'rgba(0, 180, 216, 0.08)', color: 'var(--accent-cyan)', borderColor: 'rgba(0, 180, 216, 0.2)' }}>
                      Core Fundamentals
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* IT Systems, Design & Databases Card */}
          <div className="skills-card it-focus">
            <div className="skills-card-header">
              <div className="skills-icon-wrap">
                <Terminal size={22} />
              </div>
              <div>
                <h3 className="skills-card-title">IT Support, Design & Databases</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Hardware & Software Repair, Networking Troubleshooting, Canva & CapCut</p>
              </div>
            </div>

            <div className="skills-list">
              {[...itInfraSkills, ...databaseSkills].map((skill: Skill) => (
                <div key={skill.id} className="skill-item">
                  <div className="skill-info">
                    <span className="skill-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {iconMap[skill.iconName] || <Terminal size={18} />}
                      {skill.name}
                    </span>
                    <span className="section-tag" style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem', margin: 0, background: 'rgba(127, 0, 255, 0.08)', color: 'var(--accent-violet)', borderColor: 'rgba(127, 0, 255, 0.2)' }}>
                      Practical Fundamentals
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Infinite Animated Tech Stack Carousel */}
        <TechStackCarousel />

        {/* Tech Gear Shelf */}
        <div style={{ marginTop: '4rem', textAlign: 'center' }}>
          <h3 style={{ fontSize: '1.4rem', color: 'var(--card-heading)', marginBottom: '0.5rem' }}>
            Daily Developer Tools & Ecosystem
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: '2rem' }}>
            Tools and environments I utilize daily to write clean, type-safe, and maintainable code.
          </p>

          <div className="gear-grid" style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '1rem', margin: '0 auto' }}>
            {gearTools.map((gear, idx) => (
              <div key={idx} className="gear-item">
                <div className="gear-icon">{gear.icon}</div>
                <span className="gear-name">{gear.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
